import { useState } from 'react'
import { AppDialog, Button } from '../Common'
import { useNls } from '../../nls/nlsProvider'
import {
  setEduEventRsvp,
  mapEduEventError,
  type FamilyEduEventItem,
} from '../../services/eduEventService'
import { openStorageFile } from '../../services/storageHubService'
import { formatEduDate, formatEduDateTime } from '../../utils/eduFamilyFormat'
import type { FamilyLearnerRow } from '../../services/eduFamilyService'

export interface FamilyEventDetailDialogProps {
  isOpen: boolean
  onClose: () => void
  workspaceId: string
  learnerId: string
  learner?: FamilyLearnerRow
  event?: FamilyEduEventItem | null
  onRsvpChange?: (eventId: string, response: 'GOING' | 'NOT_GOING' | 'MAYBE') => void
}

const isDeadlinePassed = (deadline?: string | null): boolean => {
  if (!deadline) return false
  return Date.now() > new Date(deadline).getTime()
}

export const FamilyEventDetailDialog = ({
  isOpen,
  onClose,
  workspaceId,
  learnerId,
  learner,
  event,
  onRsvpChange,
}: FamilyEventDetailDialogProps) => {
  const { t, locale } = useNls()

  const [submittedRsvp, setSubmittedRsvp] = useState<'GOING' | 'NOT_GOING' | 'MAYBE' | null>(null)
  const [submittingRsvp, setSubmittingRsvp] = useState(false)
  const [rsvpError, setRsvpError] = useState<string | null>(null)

  if (!event) return null

  const currentRsvp = submittedRsvp ?? event.myRsvp ?? null
  const isMinorSelf = Boolean(learner?.isMinor && learner?.relation === 'SELF')
  const isCancelled = event.status === 'CANCELLED'
  const deadlinePassed = isDeadlinePassed(event.rsvpDeadline)
  const showRsvpButtons = event.rsvpEnabled && !isCancelled && !deadlinePassed && !isMinorSelf

  const handleRsvp = async (response: 'GOING' | 'NOT_GOING' | 'MAYBE') => {
    setSubmittingRsvp(true)
    setRsvpError(null)
    try {
      await setEduEventRsvp({
        workspaceId,
        eventId: event.id,
        learnerId,
        response,
      })
      setSubmittedRsvp(response)
      onRsvpChange?.(event.id, response)
    } catch (err) {
      const nlsKey = mapEduEventError(err)
      setRsvpError(t(nlsKey))
    } finally {
      setSubmittingRsvp(false)
    }
  }

  const formatWhen = () => {
    if (event.allDay) {
      return formatEduDate(event.startAt, locale, event.timezone)
    }
    const startStr = formatEduDateTime(event.startAt, locale, event.timezone)
    if (event.endAt) {
      const endStr = formatEduDateTime(event.endAt, locale, event.timezone)
      return `${startStr} – ${endStr}`
    }
    return startStr
  }

  const rsvpLabel = (r: 'GOING' | 'NOT_GOING' | 'MAYBE' | null | undefined) => {
    switch (r) {
      case 'GOING':
        return t('NLSFamilyEventRsvpGoing')
      case 'NOT_GOING':
        return t('NLSFamilyEventRsvpNotGoing')
      case 'MAYBE':
        return t('NLSFamilyEventRsvpMaybe')
      default:
        return t('NLSFamilyEventRsvpNone')
    }
  }

  return (
    <AppDialog
      open={isOpen}
      onDismiss={onClose}
      title={event.title}
      size="md"
      actions={[
        {
          label: t('NLSEduEventCancelButton'),
          onClick: onClose,
          variant: 'secondary',
        },
      ]}
    >
      <div className="familyEventDetail">
        {isCancelled && (
          <div className="familyEventDetail__banner familyEventDetail__banner--danger" role="alert">
            <span className="familyEventDetail__bannerTitle">{t('NLSFamilyEventCancelled')}</span>
            {event.cancelReason && (
              <span className="familyEventDetail__bannerReason">
                {t('NLSFamilyEventCancelReason', { reason: event.cancelReason })}
              </span>
            )}
          </div>
        )}

        {rsvpError && (
          <div className="familyEventDetail__banner familyEventDetail__banner--danger" role="alert">
            {rsvpError}
          </div>
        )}

        <div className="familyEventDetail__metaGrid">
          <div className="familyEventDetail__metaItem">
            <span className="familyEventDetail__metaLabel">{t('NLSEduEventStartDateLabel')}</span>
            <span className="familyEventDetail__metaValue">{formatWhen()}</span>
          </div>

          {event.location && (
            <div className="familyEventDetail__metaItem">
              <span className="familyEventDetail__metaLabel">{t('NLSFamilyEventLocation')}</span>
              <span className="familyEventDetail__metaValue">{event.location}</span>
            </div>
          )}

          {event.onlineUrl && (
            <div className="familyEventDetail__metaItem">
              <span className="familyEventDetail__metaLabel">{t('NLSFamilyEventOnlineLink')}</span>
              <span className="familyEventDetail__metaValue">
                <a
                  href={event.onlineUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="familyEventDetail__link"
                >
                  {event.onlineUrl}
                </a>
              </span>
            </div>
          )}
        </div>

        {event.description && (
          <div className="familyEventDetail__section">
            <span className="familyEventDetail__metaLabel">{t('NLSEduEventDescriptionLabel')}</span>
            <p className="familyEventDetail__description">{event.description}</p>
          </div>
        )}

        {event.attachments && event.attachments.length > 0 && (
          <div className="familyEventDetail__section">
            <span className="familyEventDetail__metaLabel">{t('NLSFamilyEventAttachments')}</span>
            <div className="familyEventDetail__attachmentsList">
              {event.attachments.map((a) => (
                <Button
                  key={a.fileId}
                  variant="secondary"
                  size="sm"
                  onClick={() => void openStorageFile(a.fileId)}
                  aria-label={`${t('NLSEduEventDetailOpenAttachment')}: ${a.name}`}
                >
                  📎 {a.name}
                </Button>
              ))}
            </div>
          </div>
        )}

        {event.rsvpEnabled && (
          <div className="familyEventDetail__section">
            <div className="familyEventDetail__rsvpHeader">
              <span className="familyEventDetail__metaLabel">{t('NLSFamilyEventRsvpTitle')}</span>
              {event.rsvpDeadline && (
                <span className="familyEventDetail__rsvpDeadline">
                  {deadlinePassed
                    ? t('NLSFamilyEventRsvpClosed')
                    : t('NLSFamilyEventRsvpDeadline', {
                        deadline: formatEduDateTime(event.rsvpDeadline, locale, event.timezone),
                      })}
                </span>
              )}
            </div>

            {isMinorSelf && (
              <div className="familyEventDetail__rsvpNotice">
                <span>{t('NLSFamilyEventMinorReadOnly')}</span>
                <span className="familyEventDetail__rsvpCurrent">
                  {t('NLSFamilyEventChildAnswer')}: <strong>{rsvpLabel(currentRsvp)}</strong>
                </span>
              </div>
            )}

            {!isMinorSelf && !showRsvpButtons && (
              <div className="familyEventDetail__rsvpNotice">
                <span className="familyEventDetail__rsvpCurrent">
                  {rsvpLabel(currentRsvp)}
                </span>
              </div>
            )}

            {showRsvpButtons && (
              <div className="familyEventDetail__rsvpActions">
                <Button
                  variant={currentRsvp === 'GOING' ? 'primary' : 'secondary'}
                  size="sm"
                  disabled={submittingRsvp}
                  onClick={() => void handleRsvp('GOING')}
                >
                  {t('NLSFamilyEventRsvpGoing')}
                </Button>
                <Button
                  variant={currentRsvp === 'NOT_GOING' ? 'primary' : 'secondary'}
                  size="sm"
                  disabled={submittingRsvp}
                  onClick={() => void handleRsvp('NOT_GOING')}
                >
                  {t('NLSFamilyEventRsvpNotGoing')}
                </Button>
                <Button
                  variant={currentRsvp === 'MAYBE' ? 'primary' : 'secondary'}
                  size="sm"
                  disabled={submittingRsvp}
                  onClick={() => void handleRsvp('MAYBE')}
                >
                  {t('NLSFamilyEventRsvpMaybe')}
                </Button>
                {submittingRsvp && (
                  <span className="familyEventDetail__rsvpSubmitting">
                    {t('NLSFamilyEventSubmittingRsvp')}
                  </span>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </AppDialog>
  )
}
